<template>
  <div>
    <main-layout
        controlsBg="light"
        :hasControlsBar="false"
        :breadcrumb="getBreadcrumb">
      <div class="container">
        <grid-row  class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <div id="header">
              <h1 class="header-title">
                <chevron @click="$router.back()"/>
                {{ mode === 'create' ? $t('health.recipe.new') : $t('health.recipe.edit') }}
              </h1>
              <alert
                  v-if="alertVisible"
                  :message="alertMessage"
                  :type="alertType">
              </alert>
            </div>
          </grid-col>
        </grid-row>
        <form
            v-on:submit.prevent
            v-on:keypress.enter.prevent="onSave">
          <grid-row column-align="center" col-spaced>
            <grid-col col-size="1">
              <div class="variable-input-field variable-input-field-name">
                <label-input
                    id="nameInput"
                    ref="nameInput"
                    form-size="m"
                    :label="$t('health.recipe.name')"
                    v-model="name"
                    :margin="false"
                    :error="nameError"
                    :errorMessage="displayNameErrorMessage">
                </label-input>
              </div>

            </grid-col>
            <grid-col col-size="1">
              <div class="variable-input-field variable-input-field-name">
                <label-input
                    id="nameEnInput"
                    ref="nameEnInput"
                    form-size="m"
                    :label="$t('health.recipe.nameEn')"
                    v-model="nameEn"
                    :margin="false"
                    :error="nameEnError"
                    :errorMessage="displayNameEnErrorMessage">
                </label-input>
              </div>
            </grid-col>
            <grid-col col-size="1">
                <span
                    class="margin-right-m">
                  {{ $t('health.recipe.type') }}
                </span>
              <v-select class="v-select--light"
                        :value="selectedType" :onChange="changeType" :searchable="true"
                        :options="$t('health.nutritionalPlan.mealTypes')"></v-select>
            </grid-col>
            <grid-col col-size="1">
              <div class="center-vertical variable-input-field submit-buttons align-right">
                <btn-pill
                    :btnText="$t('health.userProfile.update')"
                    v-on:click="onSave"
                    btn-size="m">
                </btn-pill>
              </div>
            </grid-col>
          </grid-row>
          <grid-row column-align="center">
            <grid-col col-size="3">
              <grid-row>
                <grid-col col-size="3">
                  <div class="variable-input-field submit-buttons margin-bottom-xxl">
                    <checkbox
                      id="basic-cook-enabled"
                      :label="$t('health.recipe.basicCooking')"
                      v-on:change="onBasicCookChanged"
                      :checked="basicCook">
                    </checkbox>
                  </div>
                </grid-col>
              </grid-row>
              <grid-row>
                <grid-col col-size="1">
                  <div class="variable-input-field submit-buttons margin-bottom-xxl">
                    <checkbox
                        id="vegetarian-enabled"
                        :label="$t('health.recipe.vegetarian')"
                        v-on:change="onVegetarianChange"
                        :checked="vegetarian">
                    </checkbox>
                  </div>
                </grid-col>
                <grid-col col-size="1">
                  <div class="variable-input-field submit-buttons margin-bottom-xxl">
                    <checkbox
                        id="vegan-enabled"
                        :label="$t('health.recipe.vegan')"
                        v-on:change="onVeganChange"
                        :checked="vegan">
                    </checkbox>
                  </div>
                </grid-col>
                <grid-col col-size="1">
                  <div class="variable-input-field submit-buttons margin-bottom-xxl">
                    <checkbox
                      id="pescetarian-enabled"
                      :label="$t('health.recipe.pescetarian')"
                      v-on:change="onPescetarianChange"
                      :checked="pescetarian">
                    </checkbox>
                  </div>
                </grid-col>
                <grid-col col-size="1">
                  <!-- Empty column -->
                </grid-col>
              </grid-row>
              <grid-row>
                <grid-col col-size="4">
                  <multi-select :options="getTagOptions"
                                :selected-options="selectedTags"
                                placeholder="Añadir una propiedad"
                                @select="onSelectTag">
                  </multi-select>
                </grid-col>
              </grid-row>
            </grid-col>
            <grid-col col-size="1" v-if="!isNutritionist">
              <div v-if="image">
                <img :src="image" alt="Preview" class="image-preview" />
              </div>
              <btn-input-file
                class="button-uploader"
                :btn-text="$t('health.recipe.uploadImage')"
                btn-size="s"
                accept="image/*"
                @change="onImageSelected"/>
            </grid-col>
          </grid-row>
          <grid-row column-align="center">
            <grid-col col-size="2">
                <p class="center-vertical">
                  {{ $t('health.recipe.predictedScore') }}: {{ predictedScore }}
                  <span class="dot" :style="{'background-color': predictedScoreColor}"></span>
                  {{ $t('health.recipe.predictedIsp') }}: {{ predictedIsp }}
                  <span class="dot" :style="{'background-color': predictedIspColor}"></span>
                </p>
            </grid-col>
            <grid-col col-size="2">
                  <span
                      class="align-right"
                      v-tooltip.left.top-start="$t('health.nutritionalPlan.addFood')"
                      @click="showFoodModal = true"
                  >
                    <font-awesome-icon
                        class="header-add-button-icon center-vertical"
                        :icon="icon.add"/>
                  </span>
            </grid-col>
          </grid-row>
          <grid-row column-align="center">
            <grid-col col-size="4">
              <food-table
                  ref="foodTable"
                  :items="selectedFoods"
                  :noWeight="true"
                  :showFibre="true"
                  v-on:delete="deleteFood"
                  v-on:updateAmount="updateFoodAmount"
              />
            </grid-col>
          </grid-row>
          <grid-row class="margin-top-l margin-bottom-xl" column-align="center">
            <grid-col col-size="4">
              <total-macros-table
                ref="totalMacrosTable"
                :items="foodDistributionItems"
                :showFibre="true"
              />
            </grid-col>
          </grid-row>
        </form>

        <add-food-modal
            v-if="showFoodModal"
            v-model="food"
            :foods="getFilteredFoods"
            v-on:close="closeFoodModal"
            v-on:input="addFood"
        />

        <ok-cancel-modal
            v-if="showConfirmationDeleteModal"
            v-on:ok="onDeleteConfirm"
            v-on:cancel="onDeleteCancel"
            :title="deleteModalMessage"
            :message="deleteModalMessage"
            :okButton="$t('commons.delete')"
            :cancelButton="$t('commons.cancel')">
          <div slot="body">
            <ul>
              <li>
                {{ foodToDelete.food_name }}
              </li>
            </ul>
          </div>
        </ok-cancel-modal>
      </div>
    </main-layout>
  </div>
</template>

<script>

import MainLayout from '@/layouts/MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import TableComponent from '@/components/shared/table/components/TableComponent';
import TableColumn from '@/components/shared/table/components/TableColumn';
import Chevron from '@/components/shared/Chevron';
import Checkbox from '@/components/shared/forms/Checkbox';
import api from 'BRAND/api/recipe';
import nutritionApi from 'BRAND/api/nutritionPlan';
import Alert from '@/components/shared/alert/Alert';
import LabelInput from '@/components/shared/forms/LabelInput';
import vSelect from 'vue-select';
import FoodTable from 'BRAND/components/nutritionPlan/FoodTable';
import AddFoodModal from 'BRAND/components/nutritionPlan/AddFoodModal';
import OkCancelModal from '@/components/shared/dialogs/OkCancelModal';
import BtnPill from '@/components/shared/buttons/BtnPill';
import BtnInputFile from '@/components/shared/buttons/BtnInputFile';
import {faPlusCircle} from '@fortawesome/pro-solid-svg-icons';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import TotalMacrosTable from 'BRAND/components/nutritionPlan/TotalMacrosTable';
import { MultiSelect } from 'vue-search-select';
import {mapGetters} from 'vuex';

export default {
  name: 'RecipeList',
  components: {
    MainLayout,
    GridRow,
    GridCol,
    TableComponent,
    TableColumn,
    FontAwesomeIcon,
    Chevron,
    Alert,
    LabelInput,
    vSelect,
    FoodTable,
    Checkbox,
    AddFoodModal,
    OkCancelModal,
    BtnPill,
    BtnInputFile,
    TotalMacrosTable,
    MultiSelect
  },
  data () {
    return {
      mode: '',
      icon: {
        add: faPlusCircle
      },
      breadcrumb: [
        {link: '/workspace', name: this.$t('menu.workspace')},
        {link: '/recipe', name: this.$t('health.recipe.title')},
        {link: '/recipe/add', name: this.$t('health.recipe.new'), disabled: true}
      ],
      /* Alert */
      alertVisible: false,
      alertMessage: '',
      alertType: '', // error, warning, success'
      mealTypes: [
        'Desayuno',
        'Almuerzo',
        'Comida',
        'Merienda',
        'Cena'
      ],
      tagOptions: [],
      /* Modal */
      showFoodModal: false,
      food: {},
      showConfirmationDeleteModal: false,
      deleteModalMessage: '',
      /* Form */
      id: null,
      name: '',
      nameEn: '',
      type: null,
      selectedType: null,
      foods: [],
      selectedFoods: [],
      basicCook: false,
      vegetarian: false,
      vegan: false,
      pescetarian: false,
      image: '',
      imageFile: null,
      foodDistributionItems: [],
      carbsDistribution: 0,
      selectedTags: [],
      loadedTagIds: [],
      /* Food */
      totalProtein: 0,
      totalFat: 0,
      totalCarbs: 0,
      totalFibre: 0,
      predictedScoreColor: '',
      predictedScore: 0,
      predictedIspColor: '',
      predictedIsp: 0,
      /* Error */
      nameError: false,
      nameEnError: false,
      displayNameErrorMessage: '',
      displayNameEnErrorMessage: ''
    };
  },
  created () {
    if (this.$route.params.mode === undefined) {
      this.$router.push({name: 'RecipeList'});
    } else if (this.$route.params.mode === 'edit') {
      this.id = this.$route.params.id;
      this.requestRecipeData(this.id);
    }
    this.mode = this.$route.params.mode;

  },
  mounted () {
    this.requestFoodData();
    this.requestTags();
  },
  watch: {
    selectedFoods: {
      handler: function () {
        this.updateMacroTotals();
      }
    }
  },
  computed: {
    ...mapGetters([
      'getLanguageCode',
      'getUserAccoundt'
    ]),
    getBreadcrumb () {
      return this.breadcrumb;
    },
    getFilteredFoods () {
      return this.foods ? this.foods.filter(food => !this.selectedFoods.some(item => item.food_id === food.food_id)) : [];
    },
    getTagOptions () {
      return this.tagOptions;
    },
    isNutritionist() {
      const user = this.$route.params.user;
      if (!user || !user.authorities) return false;
      return user.authorities.includes('ROLE_PRESCRIPTOR');
    }
  },
  methods: {
    onSave () {
      this.clearErrors();
      if (this.name === '') {
        this.nameError = true;
        this.displayNameErrorMessage = this.$t('form.required');
      }
      if (this.nameEn === '') {
        this.nameEnError = true;
        this.displayNameEnErrorMessage = this.$t('form.required');
      }
      if (this.nameError || this.nameEnError) {
        return;
      }
      if (this.type === null) {
        showAlert(this, this.$t('health.recipe.errorNoType'), 'error');
        return;
      } else if (this.selectedFoods.length === 0) {
        showAlert(this, this.$t('health.recipe.recipeHasToHaveItemsError'), 'error');
        return;
      }

      const items = this.selectedFoods.map((item) => {
        return {
          food_id: item.food_id,
          amount: item.amount
        };
      });
      const tags = this.selectedTags.map((tag) => {
        return parseInt(tag.value);
      });
      const body = {
        name: this.name,
        name_en: this.nameEn,
        type: this.type,
        items: items,
        vegetarian: this.vegetarian,
        vegan: this.vegan,
        pescetarian: this.pescetarian,
        basicCook: this.basicCook,
        tags: tags
      };
      if (this.id == null) {
        if (!this.isNutritionist && this.imageFile === null) {
          showAlert(this, this.$t('health.recipe.errorNoImage'), 'error');
          return;
        }
        const saveCall = this.isNutritionist
          ? api.addRecipeNutri(this, body)
          : api.addRecipe(this, body);
        saveCall
          .then((data) => {
            this.id = data;
            if (this.imageFile !== null) {
              api.updateImage(this, this.id, this.imageFile)
                .then(() => {
                  showAlert(this, this.$t('health.recipe.successCreate'), 'success');
                })
                .catch(() => {
                  showAlert(this, this.$t('health.recipe.errorImageUpload'), 'error');
                });
            } else {
              showAlert(this, this.$t('health.recipe.successCreate'), 'success');
            }
          })
          .catch((error) => {
            if (error.body.message === 'Recipe must have at least one item.') {
              showAlert(this, this.$t('health.recipe.recipeHasToHaveItemsError'), 'error');
            } else {
              showAlert(this, this.$t('health.recipe.errorCreate'), 'error');
            }
          });
      } else {
        if (this.imageFile === null && this.image === '') {
          showAlert(this, this.$t('health.recipe.errorNoImage'), 'error');
          return;
        }
        api.updateRecipe(this, this.id, body)
          .then(() => {
            if (this.imageFile !== null) {
              api.updateImage(this, this.id, this.imageFile)
                .then(() => {
                  showAlert(this, this.$t('health.recipe.successUpdate'), 'success');
                })
                .catch(() => {
                  showAlert(this, this.$t('health.recipe.errorImageUpload'), 'error');
                });
            } else {
              showAlert(this, this.$t('health.recipe.successUpdate'), 'success');
            }
          })
          .catch((error) => {
            if (error.body.message === 'Recipe must have at least one item.') {
              showAlert(this, this.$t('health.recipe.recipeHasToHaveItemsError'), 'error');
            } else {
              showAlert(this, this.$t('health.recipe.errorUpdate'), 'error');
            }
          });
      }
    },
    clearErrors () {
      this.nameError = false;
      this.nameEnError = false;
      this.displayNameErrorMessage = '';
      this.displayNameEnErrorMessage = '';
    },
    changeType (type) {
      if (type === null) {
        type = this.$t('health.nutritionalPlan.mealTypes')[0];
      }
      const typeIndex = this.$t('health.nutritionalPlan.mealTypes').findIndex((e) => e === type);
      if (typeIndex === -1) {
        this.type = this.mealTypes[0];
        this.selectedType = this.$t('health.nutritionalPlan.mealTypes')[0];
      } else {
        this.type = this.mealTypes[typeIndex];
      }
    },
    deleteFood (food) {
      this.foodToDelete = food;
      this.deleteModalMessage = this.$t('health.nutritionalPlan.deleteFood');
      this.showConfirmationDeleteModal = true;
    },
    updateFoodAmount (food) {
      const languageCode = this.getLanguageCode;
      nutritionApi.getFoodInfo(this, food.food_id, languageCode).then((data) => {
        const newItem = {
          'amount': food.amount,
          'food_id': food.food_id,
          'food_name': food.food_name,
          'calories': Math.round(((data.energy_kJ_value * food.amount) / (4.184 * data.portion)) * 100) / 100,
          'protein': Math.round(((data.protein_grams_value * food.amount) / data.portion) * 100) / 100,
          'fat': Math.round(((data.fat_grams_value * food.amount) / data.portion) * 100) / 100,
          'carbs': Math.round(((data.carbohydrate_gram_value * food.amount) / data.portion) * 100) / 100,
          'fibre': Math.round(((data.fibre_gram_value * food.amount) / data.portion) * 100) / 100
        };
        const index = this.selectedFoods.findIndex((e) => e.food_id === food.food_id);
        const newSelectedFoods = this.selectedFoods;
        newSelectedFoods[index] = newItem;
        this.selectedFoods = [...newSelectedFoods];
      });
    },
    onBasicCookChanged (value) {
      this.basicCook = value;
    },
    onVegetarianChange (value) {
      this.vegetarian = value;
    },
    onVeganChange (value) {
      this.vegan = value;
    },
    onPescetarianChange (value) {
      this.pescetarian = value;
    },
    closeFoodModal () {
      this.showFoodModal = false;
      this.food = {};
    },
    onSelectTag (tags) {
      this.selectedTags = tags;
    },
    addFood (foods) {
      const foodList = Array.isArray(foods) ? foods : [foods];
      if (foodList.length === 0) return;
      const languageCode = this.getLanguageCode;
      Promise.all(foodList.map(food => nutritionApi.getFoodInfo(this, food.food_id, languageCode).then(data => ({ food, data }))))
        .then(results => {
          results.forEach(({ food, data }) => {
            this.selectedFoods.push({
              'food_id': food.food_id,
              'food_name': food.food_name,
              'amount': data.portion,
              'calories': Math.round((data.energy_kJ_value / 4.184) * 100) / 100,
              'protein': data.protein_grams_value,
              'fat': data.fat_grams_value,
              'carbs': data.carbohydrate_gram_value,
              'fibre': data.fibre_gram_value
            });
          });
        }).catch(() => {
          showAlert(this, this.$t('health.nutritionalPlan.errorAddFood'), 'error');
        });
    },
    requestFoodData () {
      const languageCode = this.getLanguageCode;
      nutritionApi.getFoodList(this, languageCode).then(data => {
        this.foods = data;
      });
    },
    requestTags () {
      const languageCode = this.getLanguageCode;
      api.getTags(this, languageCode).then(data => {
        this.tagOptions = data.items.map((tag) => {
          return {
            value: tag.id.toString(),
            text: tag.name
          };
        });
        this.updateSelectedTags();
      });
    },
    onDeleteConfirm () {
      this.showConfirmationDeleteModal = false;
      this.selectedFoods = this.selectedFoods.filter(value => value.food_id !== this.foodToDelete.food_id);
      this.foodToDelete = {};
    },
    onDeleteCancel () {
      this.foodToDelete = {};
      this.showConfirmationDeleteModal = false;
    },
    requestRecipeData (id) {
      const languageCode = this.getLanguageCode;
      api.getEditableRecipe(this, id, languageCode).then((data) => {
        console.log('recipe data:', data);
        this.name = data.name;
        this.nameEn = data.name_en;
        this.selectedType = data.type;
        this.selectedFoods = data.items;
        this.vegetarian = data.vegetarian;
        this.vegan = data.vegan;
        this.pescetarian = data.pescetarian;
        this.basicCook = data.basicCook;
        this.image = data.imageUrl;

        this.loadedTagIds = data.tags.map((tag) => tag.id);
        this.updateSelectedTags();
      });
    },
    updateSelectedTags () {
      this.selectedTags = this.tagOptions.filter((tag) => this.loadedTagIds.includes(parseInt(tag.value)));
    },
    onImageSelected (event) {
      const file = event.target.files[0];

      if (!file || !file.type.startsWith('image/')) {
        showAlert(this, this.$t('health.recipe.errorInvalidImage'), 'error');
        return;
      }

      this.imageFile = file;
      this.image = URL.createObjectURL(file);
    },
    updateMacroTotals () {
      this.totalProtein = 0;
      this.totalFat = 0;
      this.totalCarbs = 0;
      this.totalFibre = 0;
      this.selectedFoods.forEach((food) => {
        this.totalProtein += food.protein;
        this.totalFat += food.fat;
        this.totalCarbs += food.carbs;
        this.totalFibre += food.fibre;
      });
      this.requestPredictedScore();
      const totalCalories = this.totalCarbs * 4 + this.totalProtein * 4 + this.totalFat * 9;
      const totalItem = {
        'name': this.$t('health.recipe.totalGrams'),
        'proteins': Math.round(this.totalProtein * 100) / 100,
        'fats': Math.round(this.totalFat * 100) / 100,
        'carbs': Math.round(this.totalCarbs * 100) / 100,
        'calories': '-',
        'fibre': Math.round(this.totalFibre * 100) / 100
      };
      const totalKcalPerMacro = {
        'name': this.$t('health.recipe.totalKcals'),
        'proteins': Math.round(this.totalProtein * 4 * 100) / 100,
        'fats': Math.round(this.totalFat * 9 * 100) / 100,
        'carbs': Math.round(this.totalCarbs * 4 * 100) / 100,
        'calories': Math.round(totalCalories * 100) / 100,
        'fibre': '-'
      };
      this.carbsDistribution = Math.round((this.totalCarbs * 4 / totalCalories) * 100);
      const proteinDistribution = Math.round((this.totalProtein * 4 / totalCalories) * 100);
      const fatDistribution = Math.round((this.totalFat * 9 / totalCalories) * 100);
      const distributionPercentage = {
        'name': this.$t('health.recipe.kcalDistribution'),
        'proteins': proteinDistribution.toString() + '%',
        'fats': fatDistribution.toString() + '%',
        'carbs': this.carbsDistribution.toString() + '%',
        'calories': '-',
        'fibre': '-'
      };
      this.foodDistributionItems = [totalItem, totalKcalPerMacro, distributionPercentage];
    },
    requestPredictedScore () {
      const protein = Math.round(this.totalProtein * 100) / 100;
      const fat = Math.round(this.totalFat * 100) / 100;
      const carbs = Math.round(this.totalCarbs * 100) / 100;
      const fibre = Math.round(this.totalFibre * 100) / 100;
      api.getPredictedScore(this, protein, fat, carbs, fibre).then((data) => {
        this.predictedScoreColor = data.color;
        this.predictedScore = Math.round(data.score * 100) / 100;
        this.predictedIsp = Math.round(data.isp * 100) / 100;
        // ISP color logic: same ranges as score, but inverted colors (higher ISP is better)
        // ISP < 31 → red (bad)
        // ISP 31-60 → yellow (moderate)
        // ISP > 60 → green (good)
        if (data.isp < 31) {
          this.predictedIspColor = 'red';
        } else if (data.isp <= 60) {
          this.predictedIspColor = 'yellow';
        } else {
          this.predictedIspColor = 'green';
        }
      });
    }
  }
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
  }, 3000);
};

</script>

<style lang="scss" scoped>
@import "../../../../stylesheets/variables";

.header-add-button-icon {
  font-size: 3rem;
  color: $secondary;
  // Align to the right
  align-content: end;
}

.align-right {
  float: right;
  clear: both;
}

.image-preview {
  max-width: 100%;
  max-height: 100%;
}

.dot {
  height: 25px;
  width: 25px;
  border-radius: 50%;
  display: inline-block;
}

.center-vertical {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
</style>



// WEBPACK FOOTER //
// src/brand/health/layouts/recipe/EditRecipe.vue